import { broker } from '#~/agent/broker.js';
// ENG-4154: read the IdentityState store, not a per-consumer bridge poll.
import { resolveLinkedAgentId } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import * as localStorage from '#~/persistence/index.js';
import type { BrowserMediaDevice } from './types.js';
import { generateId } from '@studio/models';
import type { T_Maybe } from '@tryiris-ai/js-types';

let localSessionID = '';

export const getLocalSessionID = () => {
  if (localSessionID === '') {
    localSessionID = generateId();
  }

  return localSessionID;
};

// Custom Base64 encoding function that supports UTF-8
function utoa(str: string): string {
  return window.btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    )
  );
}

let cachedMachinePersistentID: T_Maybe<string>;

export const safeMachinePersistentID = async (): Promise<string> => {
  if (cachedMachinePersistentID) {
    return cachedMachinePersistentID;
  }

  const machinePersistentID = await localStorage.get('browserSourcedDevicesMachinePersistentID');

  if (machinePersistentID && typeof machinePersistentID === 'string') {
    cachedMachinePersistentID = machinePersistentID;
    return machinePersistentID;
  }

  const nextMachinePersistentID = generateId();

  await localStorage.set('browserSourcedDevicesMachinePersistentID', nextMachinePersistentID);

  cachedMachinePersistentID = nextMachinePersistentID;
  return nextMachinePersistentID;
};

export const discoverMediaDevices = async (): Promise<BrowserMediaDevice[]> => {
  if (!isElectronApp('worker')) {
    return [];
  }

  const linkedEncoderID = await resolveLinkedAgentId();

  const linkedAgentExistsInRoom =
    Array.from(broker).find((proxy) => proxy.id === linkedEncoderID) !== undefined;

  if (!linkedAgentExistsInRoom) {
    return [];
  }

  const devices = await navigator.mediaDevices.enumerateDevices();

  const kindMapping = {
    audioinput: 'audio',
    videoinput: 'video'
  };

  const machinePersistentID = await safeMachinePersistentID();

  const filteredDevices = devices.filter(({ kind }) => Object.keys(kindMapping).includes(kind));
  return filteredDevices.map((device, index) => ({
    id: device.deviceId,
    label: device.label,
    persistentID: utoa(
      JSON.stringify({
        machinePersistentID,
        machineSessionID: getLocalSessionID(),
        kind: device.kind,
        label: device.label,
        offset: filteredDevices.slice(0, index).filter(({ label }) => label === device.label).length
      })
    ),
    // @ts-ignore
    kind: kindMapping[device.kind]
  }));
};

export const customBase64Decode = (str: string): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
  let output = '';
  str = str.replace(/=+$/, '');

  if (str.length % 4 === 1) {
    throw new Error("'atob' failed: The string to be decoded is not correctly encoded.");
  }

  for (let bc = 0, bs = 0, buffer, i = 0; (buffer = str.charAt(i++)); ) {
    const idx = chars.indexOf(buffer);
    if (idx === -1) {
      throw new Error("'atob' failed: The string to be decoded is not correctly encoded.");
    }
    bs = bc % 4 ? bs * 64 + idx : idx;
    if (bc++ % 4) {
      output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6)));
    }
  }

  return output;
};

export const locateDeviceByPersistentID = async (
  kind: 'audio' | 'video',
  persistentID: string
): Promise<BrowserMediaDevice | undefined> => {
  try {
    const decodedString = customBase64Decode(persistentID);

    const { label, offset } = JSON.parse(decodedString);
    const devices = await discoverMediaDevices();
    const candidates = devices.filter((device) => {
      const decodedDetails = customBase64Decode(device.persistentID);
      const { label: deviceLabel } = JSON.parse(decodedDetails);
      return deviceLabel === label && device.kind === kind;
    });
    return candidates[offset] ?? candidates[0];
  } catch (error) {
    console.error('Error in locateDeviceByPersistentID:', error);
    throw error;
  }
};

export const extractMachineIDAndTypeFromPersistentID = (
  persistentID: string
): {
  machineSessionID: string;
  machinePersistentID: string;
  kind: 'audio' | 'video';
  label: string;
  offset: number;
} => {
  const decodedString = customBase64Decode(persistentID);

  let { machineSessionID, machinePersistentID, kind, label, offset } = JSON.parse(decodedString);

  if (kind === 'audioinput') kind = 'audio';
  if (kind === 'videoinput') kind = 'video';

  return { machineSessionID, machinePersistentID, kind, label, offset };
};
